import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { Component, computed, inject, signal, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatDivider, MatList, MatListItem, MatListOption, MatSelectionList, MatSelectionListChange } from '@angular/material/list';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatFormField, MatHint, MatLabel, MatOption, MatPrefix, MatSelect, MatSuffix } from '@angular/material/select';
import { MatStep, MatStepContent, MatStepLabel, MatStepper } from '@angular/material/stepper';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { WireFeedTypes } from '@common/enums/wire-feed-types';
import { toggleSelectedIds } from '@common/utils/util';
import { ApProduct, DataminrFeedLink, WireCredentials, WireFeed } from '@discover/store/models/feed.provider.models';
import { LetDirective, PushPipe } from '@ngrx/component';
// import {
//     selectAccessTokens,
//     selectFeedProviderIsLoading,
//     selectFeedType,
//     selectFilterApProducts,
//     selectHasNoSelectedApProducts,
//     selectIsAccessTokenOpen,
//     selectIsNotLoading,
//     selectProviders,
// } from '@discover/store/selectors/feed.discover.selectors';
import { Store } from '@ngrx/store';
import { ModalResponseCodes, ModalService } from '@src/suite/common/components/modal/modal.service';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { FeedProviderActions, FeedProviderApiActions } from '@src/suite/discover/store/actions/feed.provider.actions';
import {
    selectApCredentials,
    selectApiKey,
    selectApProducts,
    selectApSelectedProducts,
    selectCredentials,
    selectCredentialsTitle,
    selectCredentialsType,
    selectDataminrClientId,
    selectDataminrClientSecret,
    selectDataminrCredentials,
    selectDataminrLinks,
    selectDataminrSelectedLinks,
    selectFeedProviderIsLoading,
    selectFeeds,
    selectFeedTitle,
    selectFeedType,
    selectFtpDirectory,
    selectFtpPassword,
    selectFtpProvider,
    selectFtpProviders,
    selectFtpUrl,
    selectFtpUsername,
    selectHasSelectedApProducts,
    selectHasSelectedDataminrLinks,
    selectInEditMode,
    selectIsAccessTokenEnabled,
    selectIsApiKeyDisabled,
    selectIsCredentialsDialogOpen,
    selectIsFeedDialogOpen,
    selectIsInitialDialogOpen,
    selectIsLinkStepVisible,
    selectIsNotLoading,
    selectIsProductStepVisible,
    selectIsSaveButtonDisabled,
    selectIsSelectLinksEnabled,
    selectIsSelectProductsEnabled,
    selectTabIndex,
} from '@src/suite/discover/store/selectors/feed.discover.selectors';
import { combineLatest, firstValueFrom, map, Observable } from 'rxjs';

import { StTextAreaOverlayComponent } from '../../../common/controls/st-text-area/text-area-overlay/st-text-area-overlay';
import { TextFieldComponent } from '../../../common/controls/text-field/text-field';
import { LoaderComponent } from '../../../loader/loader';

const FTP_URL = /^((?:ftp):\/\/)(?:(?:[A-Z0-9](?:[A-Z0-9-]{0,61}[A-Z0-9])?\.)+(?:[A-Z]{2,6}\.?|[A-Z0-9-]{2,}\.?)|localhost|\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})(?::\d+)?(?:\/?|[/?]\S+)$/i;
const SFTP_URL = /^((?:sftp):\/\/)(?:(?:[A-Z0-9](?:[A-Z0-9-]{0,61}[A-Z0-9])?\.)+(?:[A-Z]{2,6}\.?|[A-Z0-9-]{2,}\.?)|localhost|\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})(?::\d+)?(?:\/?|[/?]\S+)$/i;

@Component({
    selector: 'st-discover-admin-settings-dialog;',
    imports: [
        CdkOverlayOrigin,
        FormsModule,
        LetDirective,
        LoaderComponent,
        MatButton,
        MatCheckbox,
        MatDialogClose,
        MatDivider,
        MatFormField,
        MatHint,
        MatIcon,
        MatIconButton,
        MatInput,
        MatLabel,
        MatList,
        MatListItem,
        MatListOption,
        MatOption,
        MatPrefix,
        MatRadioButton,
        MatRadioGroup,
        MatSelect,
        MatSelectionList,
        MatStep,
        MatStepContent,
        MatStepLabel,
        MatStepper,
        MatSuffix,
        MatTab,
        MatTabGroup,
        PushPipe,
        ReactiveFormsModule,
        StTextAreaOverlayComponent,
        TextFieldComponent,
    ],
    templateUrl: './discover-admin-settings-dialog.html',
    styleUrl: './discover-admin-settings-dialog.scss',
    encapsulation: ViewEncapsulation.None,
    host: { class: 'st-discover-admin-settings-dialog' },
})
export class DiscoverAdminSettingsDialogComponent {
    dialogRef = inject<MatDialogRef<DiscoverAdminSettingsDialogComponent>>(MatDialogRef);
    private modalService = inject(ModalService);
    private store = inject(Store);

    readonly wireFeedTypes = WireFeedTypes;

    public ftpForm = new FormGroup({
        provider: new FormControl(null, Validators.required),
        url: new FormControl(null, [Validators.required, this.createFTPOrSFTPValidator]),
        username: new FormControl(null, [Validators.required]),
        password: new FormControl(null, Validators.required),
        directory: new FormControl(null),
    });

    searchProduct = new FormControl<string | number>('');
    searchLink = new FormControl<string | number>('');

    public apiKey = '';
    public dataminrClientId = '';
    public dataminrClientSecret = '';

    private readonly linkSearchTerm = signal('');
    private readonly productSearchTerm = signal('');

    isLoading$ = this.store.select(selectFeedProviderIsLoading);
    isNotLoading$ = this.store.select(selectIsNotLoading);
    isInitialDialogOpen$ = this.store.select(selectIsInitialDialogOpen);
    isCredentialsDialogOpen$ = this.store.select(selectIsCredentialsDialogOpen);

    selectedTabIndex$ = this.store.select(selectTabIndex);

    isFeedDialogOpen$ = this.store.select(selectIsFeedDialogOpen);
    inEditMode$ = this.store.select(selectInEditMode);
    isProductsStepVisible$ = this.store.select(selectIsProductStepVisible);
    isLinksStepVisible$ = this.store.select(selectIsLinkStepVisible);

    isAccessTokenEnabled$ = this.store.select(selectIsAccessTokenEnabled);
    isSelectProductsEnabled$ = this.store.select(selectIsSelectProductsEnabled);
    isSelectLinksEnabled$ = this.store.select(selectIsSelectLinksEnabled);

    isApiKeyDisabled$ = this.store.select(selectIsApiKeyDisabled);
    isSaveButtonDisabled$ = this.store.select(selectIsSaveButtonDisabled);

    credentials$ = this.store.select(selectCredentials);
    apCredentials$ = this.store.select(selectApCredentials);
    credentialsTitle$ = this.store.select(selectCredentialsTitle);
    credentialsType = this.store.selectSignal(selectCredentialsType);
    apiKey$ = this.store.select(selectApiKey);
    dataminrClientId$ = this.store.select(selectDataminrClientId);
    dataminrClientSecret$ = this.store.select(selectDataminrClientSecret);
    dataminrCredentials$ = this.store.select(selectDataminrCredentials);

    feeds$ = this.store.select(selectFeeds);

    feedTitle$ = this.store.select(selectFeedTitle);
    feedType$ = this.store.select(selectFeedType);

    ftpProviders$ = this.store.select(selectFtpProviders);
    ftpProvider$ = this.store.select(selectFtpProvider);
    ftpUrl$ = this.store.select(selectFtpUrl);
    ftpUsername$ = this.store.select(selectFtpUsername);
    ftpPassword$ = this.store.select(selectFtpPassword);
    ftpDirectory$ = this.store.select(selectFtpDirectory);
    selectHasSelectedApProducts$ = this.store.select(selectHasSelectedApProducts);
    selectHasSelectedDataminrLinks$ = this.store.select(selectHasSelectedDataminrLinks);

    languageSettings = this.store.selectSignal(selectI18N)();
    apProductsList = this.store.selectSignal(selectApProducts);
    dataminrLinksList = this.store.selectSignal(selectDataminrLinks);
    dataminrSelectedLinks = this.store.selectSignal(selectDataminrSelectedLinks);
    apSelectedProducts = this.store.selectSignal(selectApSelectedProducts);

    readonly apProducts = computed(() => {
        const term = this.productSearchTerm().toLowerCase();
        return this.apProductsList().filter(product => product.name.toLowerCase().includes(term));
    });
    readonly dataminrLinks = computed(() => {
        const term = this.linkSearchTerm().toLowerCase();
        return this.dataminrLinksList().filter(link => link.name.toLowerCase().includes(term));
    });

    constructor() {
        this.store.dispatch(FeedProviderActions.initDialog());
    }

    get ftpOrSftpHint(): Observable<string> {
        return this.feedType$.pipe(
            map(type => {
                if (type === WireFeedTypes.FTP) return 'ftp(s)://example.com';
                if (type === WireFeedTypes.SFTP) return 'sftp://example.com';
                return '';
            })
        );
    }

    get isFeedSaveButtonDisabled$(): Observable<boolean> {
        return combineLatest([this.feedType$, this.selectHasSelectedApProducts$, this.selectHasSelectedDataminrLinks$, this.feedTitle$]).pipe(
            map(([feedType, apHasSelectedProducts, dataminrHasSelectedLinks, feedTitle]) => {
                if (!feedTitle || feedTitle?.trim().length === 0) return true;
                if (feedType === WireFeedTypes.FTP || feedType === WireFeedTypes.SFTP) {
                    return !this.ftpForm.valid;
                }
                if (feedType === WireFeedTypes.AP) {
                    return !apHasSelectedProducts;
                }
                if (feedType === WireFeedTypes.DATAMINR) {
                    return !dataminrHasSelectedLinks;
                }
                return true;
            })
        );
    }

    saveCredentials() {
        this.store.dispatch(FeedProviderActions.saveWireCredentials());
    }

    saveFeed() {
        this.store.dispatch(FeedProviderActions.saveWireFeed());
    }

    cancel() {
        this.store.dispatch(FeedProviderActions.resetDialog());
    }

    addCredentials() {
        this.store.dispatch(FeedProviderActions.openAddCredentialsDialog());
    }

    openAddFeedDialog() {
        this.store.dispatch(FeedProviderActions.openAddFeedDialog());
    }

    createFTPOrSFTPValidator() {
        return (control: AbstractControl): Observable<ValidationErrors> => {
            return this.feedType$.pipe(
                map(type => {
                    const value = control.value;
                    let urlPattern: RegExp;

                    if (!value) {
                        return null;
                    }

                    if (type === WireFeedTypes.FTP) {
                        urlPattern = new RegExp(FTP_URL);
                    }
                    if (type === WireFeedTypes.SFTP) {
                        urlPattern = new RegExp(SFTP_URL);
                    }

                    const urlValid = urlPattern.test(value);
                    return !urlValid ? { urlPattern: true } : null;
                })
            );
        };
    }

    updateCredentialsType(feedType: WireFeedTypes) {
        this.store.dispatch(
            FeedProviderActions.updateCredentialsType({
                credType: feedType,
            })
        );
    }

    updateFeedType(feedType: WireFeedTypes) {
        this.store.dispatch(FeedProviderActions.updateFeedType({ feedType: feedType }));
    }

    onSelectAll(checked: boolean, items: MatSelectionList, feedType: WireFeedTypes) {
        if (checked) {
            items.selectAll();
        } else {
            items.deselectAll();
        }
        if (feedType === WireFeedTypes.AP) {
            this.store.dispatch(FeedProviderActions.updateSelectedProducts({ products: toggleSelectedIds(this.apSelectedProducts(), items.options.toArray()) }));
        } else if (feedType === WireFeedTypes.DATAMINR) {
            this.store.dispatch(FeedProviderActions.updateSelectedLinks({ links: toggleSelectedIds(this.dataminrSelectedLinks(), items.options.toArray()) }));
        }
    }

    public onSelectedProductsChange(event: MatSelectionListChange) {
        this.store.dispatch(FeedProviderActions.updateSelectedProducts({ products: toggleSelectedIds(this.apSelectedProducts(), event.options) }));
    }

    public onSelectedLinksChange(event: MatSelectionListChange) {
        this.store.dispatch(FeedProviderActions.updateSelectedLinks({ links: toggleSelectedIds(this.dataminrSelectedLinks(), event.options) }));
    }

    public async editCredentials(credentials: WireCredentials) {
        this.store.dispatch(FeedProviderActions.openEditCredentialsDialog({ credentials: credentials }));
    }

    public async editFeed(feed: WireFeed) {
        this.store.dispatch(FeedProviderActions.editWireFeed({ feedId: feed.id, feedType: feed.type, credentialsId: feed.wireCredentialsId }));
    }

    public async removeCredentials(credentials: WireCredentials) {
        const modalContent = `${this.languageSettings.PSTWebConfirmDeletePart1} ${credentials.name} ${this.languageSettings.PSTWebConfirmDeleteKey}?`;
        const modalRef = this.modalService.openModal({
            title: this.languageSettings.PSTWebConfirmDeleteLabel,
            content: modalContent,
        }).result;
        let modalRes;
        try {
            modalRes = await firstValueFrom(modalRef);
        } catch (_er) {
            modalRes = ModalResponseCodes.CANCEL;
        }

        if (modalRes === ModalResponseCodes.OK) {
            this.store.dispatch(FeedProviderApiActions.deleteCredentials({ guid: credentials.wireCredentialsId, ftype: credentials.feedType }));
        }
    }

    public async removeFeed(feed: WireFeed) {
        const modalContent = `${this.languageSettings.PSTWebConfirmDeletePart1} ${feed.name}?`;
        const modalRef = this.modalService.openModal({
            title: this.languageSettings.PSTWebConfirmDeleteLabel,
            content: modalContent,
        }).result;
        let modalRes;
        try {
            modalRes = await firstValueFrom(modalRef);
        } catch (_er) {
            modalRes = ModalResponseCodes.CANCEL;
        }

        if (modalRes === ModalResponseCodes.OK) {
            this.store.dispatch(FeedProviderApiActions.deleteFeed({ feedId: feed.id, feedType: feed.type }));
        }
    }

    public onSelectAccessTokenClicked(stepper: MatStepper) {
        stepper.next();
    }

    public onSelectProductsClicked(stepper: MatStepper) {
        stepper.next();
    }

    public onSelectLinksClicked(stepper: MatStepper) {
        stepper.next();
    }

    public onCredentialsSelected(event: MatSelectionListChange, feedType: WireFeedTypes) {
        if (feedType === WireFeedTypes.AP) {
            this.store.dispatch(FeedProviderActions.updateApCredentailsId({ apCredentialsId: event.options[0].value }));
        }
        if (feedType === WireFeedTypes.DATAMINR) {
            this.store.dispatch(FeedProviderActions.updateDataminrCredentialsId({ dataminrCredentialsId: event.options[0].value }));
        }
    }

    updateCredentialsTitle(value: string) {
        this.store.dispatch(FeedProviderActions.updateCredentialsTitle({ credTitle: value }));
    }

    updateFeedTitle(value: string) {
        this.store.dispatch(FeedProviderActions.updateFeedTitle({ feedTitle: value }));
    }

    public updateApiKey(value: string) {
        this.apiKey = value;
        this.store.dispatch(FeedProviderActions.updateCredentialsApiKey({ apiKey: this.apiKey }));
    }

    public updateDataminrClientId(value: string) {
        this.store.dispatch(FeedProviderActions.updateDataminrClientId({ clientId: value }));
    }

    public updateDataminrClientSecret(value: string) {
        this.store.dispatch(FeedProviderActions.updateDataminrClientSecret({ clientSecret: value }));
    }

    public updateFtpProvider(value: string) {
        this.store.dispatch(FeedProviderActions.updateFtpProvider({ ftpProvider: value }));
    }

    public updateFtpUrl(event: any) {
        this.store.dispatch(FeedProviderActions.updateFtpUrl({ ftpUrl: event.target.value }));
    }

    public updateFtpUsername(event: any) {
        this.store.dispatch(FeedProviderActions.updateFtpUsername({ ftpUsername: event.target.value }));
    }

    public updateFtpPassword(event: any) {
        this.store.dispatch(FeedProviderActions.updateFtpPassword({ ftpPassword: event.target.value }));
    }

    public updateFtpDirectory(event: any) {
        this.store.dispatch(FeedProviderActions.updateFtpDirectory({ ftpDirectory: event.target.value }));
    }

    filterProducts(value: string) {
        this.productSearchTerm.set(value);
        if (!value) {
            this.searchProduct.reset();
        }
    }

    filterLinks(value: string) {
        this.linkSearchTerm.set(value);
        if (!value) {
            this.searchLink.reset();
        }
    }

    productById(_index: number, product: ApProduct) {
        return product.id;
    }

    linkById(_index: number, link: DataminrFeedLink) {
        return link.id;
    }
}
